import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './RekordboxUpdateModal.scss';

// One field line inside a track row: "TITLE  old → new" when the fix writes
// it, "TITLE  value  unchanged" when Rekordbox's value is kept.
const FieldDiff = ({ label, changed, oldVal, oldMissing, newVal }) => (
    <div className="rb-field-row">
        <span className="rb-field-label">{label}</span>
        {changed ? (
            <span className="rb-field-diff">
                <span className={`rb-field-old${oldMissing ? ' rb-field-missing' : ''}`}>{oldVal}</span>
                <span className="rb-field-arrow">→</span>
                <span className="rb-field-new">{newVal}</span>
            </span>
        ) : (
            <span className="rb-field-diff">
                <span className={`rb-field-kept${oldMissing ? ' rb-field-missing' : ''}`}>{oldVal}</span>
                <span className="rb-field-kept-note">unchanged</span>
            </span>
        )}
    </div>
);

/**
 * Confirmation for "Update Rekordbox": shows, per flagged track, a per-field
 * diff of Rekordbox's row (old → new for fields the fix writes, dimmed
 * "unchanged" for kept ones), with per-row opt-out. Writes nothing itself —
 * onConfirm receives the checked filePaths and the caller runs the sync.
 */
const RekordboxUpdateModal = ({ show, items, syncing, rekordboxOpen, onClose, onConfirm }) => {
    const [checked, setChecked] = useState(() => new Set());

    // Re-arm the selection (everything checked) each time the modal opens.
    useEffect(() => {
        if (show) setChecked(new Set((items || []).map((i) => i.filePath)));
    }, [show, items]);

    const toggle = (filePath) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(filePath)) next.delete(filePath); else next.add(filePath);
            return next;
        });
    };

    const count = checked.size;

    return (
        <Modal show={show} onHide={syncing ? undefined : onClose} centered size="lg" className="rb-update-modal">
            <Modal.Header closeButton={!syncing}>
                <Modal.Title>Fix Rekordbox&apos;s library</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="rb-update-intro">
                    These tracks are tagged correctly in the file, but Rekordbox still shows the raw
                    filename. Rekordbox&apos;s library will be updated from the files&apos; tags.
                    No audio files are modified.
                </p>
                <div className="rb-update-list">
                    {(items || []).map((item) => (
                        <label key={item.filePath} className="rb-update-row">
                            <input
                                type="checkbox"
                                checked={checked.has(item.filePath)}
                                onChange={() => toggle(item.filePath)}
                                disabled={syncing}
                            />
                            <div className="rb-update-row-body">
                                <div className="rb-update-file" title={item.filePath}>{item.fileName}</div>
                                {/* Per-field diff of Rekordbox's row: changed fields read
                                    old → new (the app's arrow idiom); kept fields stay
                                    dim with an explicit "unchanged". */}
                                <div className="rb-update-diff">
                                    <FieldDiff
                                        label="Title"
                                        changed={item.issues?.junkTitle ?? true}
                                        oldVal={item.rbTitle || '—'}
                                        newVal={item.newTitle}
                                    />
                                    <FieldDiff
                                        label="Artist"
                                        changed={item.issues?.missingArtist ?? true}
                                        oldVal={item.rbArtist || 'no artist'}
                                        oldMissing={!item.rbArtist}
                                        newVal={item.newArtist}
                                    />
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer className="rb-update-footer">
                {rekordboxOpen && (
                    <span className="rb-update-note">
                        Rekordbox is open — updates will be applied automatically when you close it.
                    </span>
                )}
                <Button variant="secondary" onClick={onClose} disabled={syncing}>
                    Cancel
                </Button>
                <Button variant="primary" disabled={count === 0 || syncing} onClick={() => onConfirm([...checked])}>
                    {syncing ? 'Fixing…' : `Fix ${count} track${count !== 1 ? 's' : ''} in Rekordbox`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RekordboxUpdateModal;
